import type { DeviceFieldName, DeviceKind, FieldConfig } from '../types/hook-types';

// Base field definitions with all metadata
export const fieldDefinitions: Record<DeviceFieldName, Omit<FieldConfig, 'name'>> = {
    identifier: {
        label: 'Naam',
        type: 'text',
        width: '14%',
        minWidth: '8ch',
    },
    residualCurrentDeviceType: {
        label: 'Type',
        type: 'select',
        width: '10%',
        minWidth: '5ch',
    },
    ratedCurrent: {
        label: 'Δ Waarde (A)',
        type: 'select',
        width: '12%',
        minWidth: '9ch',
    },
    tripDelay: {
        label: 'Δt (ms)',
        type: 'number',
        width: '10%',
        minWidth: '7ch',
    },
    isTestButtonFunctional: {
        label: 'Testknop',
        type: 'boolean-select',
        width: '15%',
        minWidth: '6ch',
    },
    fuseKind: {
        label: 'Type beveiliging',
        type: 'select',
        width: '12%',
        minWidth: '8ch',
    },
    trippingCharacteristic: {
        label: 'Soort',
        type: 'select',
        width: '10%',
        minWidth: '4ch',
        dependsOn: ['fuseKind'],
    },
    breakerRatedCurrent: {
        label: 'Waarde',
        type: 'select',
        width: '10%',
        minWidth: '5ch',
        dependsOn: ['fuseKind', 'trippingCharacteristic'],
    },
    powerPhaseKind: {
        label: 'Licht / Kracht',
        type: 'select',
        width: '15%',
        minWidth: '7ch',
    },
    risoValue: {
        label: 'Riso (MΩ)',
        type: 'riso',
        width: '20%',
        minWidth: '12ch',
    },
    // FCPD impedance loop fields - Zi
    ziResistance: {
        label: 'Zi (Ω)',
        type: 'number',
        width: '10%',
        minWidth: '8ch',
    },
    ziCurrent: {
        label: 'Ik (Zi)',
        type: 'number',
        width: '10%',
        minWidth: '8ch',
    },
    ziMinimalCurrent: {
        label: 'Acceptatiecriteria Zi',
        type: 'number',
        width: '12%',
        minWidth: '10ch',
    },
    // FCPD impedance loop fields - Zs
    zsResistance: {
        label: 'Zs (Ω)',
        type: 'number',
        width: '10%',
        minWidth: '8ch',
    },
    zsCurrent: {
        label: 'Ik (Zs)',
        type: 'number',
        width: '10%',
        minWidth: '8ch',
    },
    zsMinimalCurrent: {
        label: 'Acceptatiecriteria Zs',
        type: 'number',
        width: '12%',
        minWidth: '10ch',
    },
};

// Fields per device kind (in display order)
export const deviceFieldMap: Record<DeviceKind, DeviceFieldName[]> = {
    ResidualCurrentDevice: [
        'identifier',
        'residualCurrentDeviceType',
        'ratedCurrent',
        'tripDelay',
        'isTestButtonFunctional',
        'risoValue', // Conditional based on enableExtendedRiso
    ],
    ResidualCurrentCircuitBreakerDevice: [
        'identifier',
        'residualCurrentDeviceType',
        'ratedCurrent',
        'trippingCharacteristic',
        'breakerRatedCurrent',
        'tripDelay',
        'isTestButtonFunctional',
        'risoValue', // Conditional based on enableExtendedRiso
    ],
    FinalCircuitProtectiveDevice: [
        'identifier',
        'fuseKind',
        'trippingCharacteristic',
        'breakerRatedCurrent',
        'risoValue', // Conditional based on enableExtendedRiso
        // Impedance fields - conditional based on enableImpedanceMeasurementsPage
        'ziResistance',
        'ziCurrent',
        'ziMinimalCurrent',
        'zsResistance',
        'zsCurrent',
        'zsMinimalCurrent',
    ],
};

// Fields that are shown only when impedance measurements page is disabled
export const fcpdImpedanceFields: DeviceFieldName[] = [
    'ziResistance',
    'ziCurrent',
    'ziMinimalCurrent',
    'zsResistance',
    'zsCurrent',
    'zsMinimalCurrent',
];

// Custom column sizing overrides per device kind
export const deviceKindColumnSizing: Record<DeviceKind, Partial<Record<DeviceFieldName, { width: string; minWidth: string }>>> = {
    ResidualCurrentDevice: {
        identifier: { width: '14%', minWidth: '8ch' },
        residualCurrentDeviceType: { width: '14%', minWidth: '5ch' },
        ratedCurrent: { width: '14%', minWidth: '9ch' },
        tripDelay: { width: '14%', minWidth: '7ch' },
        isTestButtonFunctional: { width: '20%', minWidth: '6ch' },
        risoValue: { width: '20%', minWidth: '12ch' },
    },
    ResidualCurrentCircuitBreakerDevice: {
        identifier: { width: '12%', minWidth: '8ch' },
        residualCurrentDeviceType: { width: '8%', minWidth: '5ch' },
        ratedCurrent: { width: '10%', minWidth: '9ch' },
        trippingCharacteristic: { width: '8%', minWidth: '4ch' },
        breakerRatedCurrent: { width: '8%', minWidth: '5ch' },
        tripDelay: { width: '8%', minWidth: '7ch' },
        isTestButtonFunctional: { width: '15%', minWidth: '6ch' },
        risoValue: { width: '24%', minWidth: '12ch' },
    },
    FinalCircuitProtectiveDevice: {
        identifier: { width: '5%', minWidth: '5ch' },
        fuseKind: { width: '12%', minWidth: '12ch' },
        trippingCharacteristic: { width: '8%', minWidth: '8ch' },
        breakerRatedCurrent: { width: '8%', minWidth: '8ch' },
        risoValue: { width: '12%', minWidth: '10ch' },
        // Impedance fields sizing (when shown)
        ziResistance: { width: '8%', minWidth: '8ch' },
        ziCurrent: { width: '8%', minWidth: '8ch' },
        ziMinimalCurrent: { width: '14%', minWidth: '14ch' },
        zsResistance: { width: '8%', minWidth: '8ch' },
        zsCurrent: { width: '8%', minWidth: '8ch' },
        zsMinimalCurrent: { width: '14%', minWidth: '14ch' },
    },
};

export type GetVisibleFieldsOptions = {
    enableExtendedRiso: boolean;
    /**
     * Only applies to FinalCircuitProtectiveDevice.
     * When true, impedance measurements are on a separate page, so hide Zi/Zs columns.
     * When false, show Zi/Zs columns in the table.
     */
    enableImpedanceMeasurementsPage?: boolean;
};

/**
 * Get visible fields for a device kind, considering feature flags.
 *
 * - When enableExtendedRiso is true, there's a separate page for Riso measurements,
 *   so we hide the risoValue field from the table.
 * - For FCPD: when enableImpedanceMeasurementsPage is true, hide Zi/Zs columns
 *   (they have their own page). When false, show them as table columns.
 */
export function getVisibleFields(deviceKind: DeviceKind, options: boolean | GetVisibleFieldsOptions): DeviceFieldName[] {
    // Support legacy boolean parameter for backwards compatibility
    const opts: GetVisibleFieldsOptions = typeof options === 'boolean'
        ? { enableExtendedRiso: options }
        : options;

    const { enableExtendedRiso, enableImpedanceMeasurementsPage = true } = opts;

    let fields = [...deviceFieldMap[deviceKind]];

    // Hide risoValue when extended Riso page is enabled
    if (enableExtendedRiso) {
        fields = fields.filter(f => f !== 'risoValue');
    }

    // For FCPD: hide impedance fields when impedance measurements page is enabled
    if (deviceKind === 'FinalCircuitProtectiveDevice' && enableImpedanceMeasurementsPage) {
        fields = fields.filter(f => !fcpdImpedanceFields.includes(f));
    }

    return fields;
}

/**
 * Get field configs for a device kind with proper sizing.
 */
export function getFieldConfigs(deviceKind: DeviceKind, options: boolean | GetVisibleFieldsOptions): FieldConfig[] {
    const visibleFields = getVisibleFields(deviceKind, options);
    const kindOverrides = deviceKindColumnSizing[deviceKind];

    return visibleFields.map((fieldName) => {
        const baseDefinition = fieldDefinitions[fieldName];
        const sizeOverride = kindOverrides[fieldName];

        return {
            name: fieldName,
            ...baseDefinition,
            ...(sizeOverride ?? {}),
        };
    });
}

/**
 * Helper to get column sizing for a specific device kind and field.
 */
export function getColumnSizing(deviceKind: DeviceKind, fieldName: DeviceFieldName): { width: string; minWidth: string } {
    const kindOverride = deviceKindColumnSizing[deviceKind]?.[fieldName];
    return kindOverride ?? {
        width: fieldDefinitions[fieldName].width,
        minWidth: fieldDefinitions[fieldName].minWidth,
    };
}
